import { lazy, Suspense, type ReactNode } from "react";
import {
  Navigate,
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
} from "react-router-dom";
import { Toaster } from "@fox1ck-org/ui/toast";
import { TrackerShell } from "@/shell/tracker-shell";
import { SignedInArea } from "@/shell/sign-in";
import { Skeleton } from "@fox1ck-org/ui";
import DashboardPage from "@/pages/dashboard";
import CampaignsListPage from "@/pages/campaigns-list";
import CampaignCardPage from "@/pages/campaign/card";
import LandersListPage from "@/pages/assets/landers-list";
import TrafficSourcesListPage from "@/pages/assets/traffic-sources-list";
import OffersPage from "@/pages/assets/offers/list";
import PartnersPage from "@/pages/assets/networks/list";
import LogsPage from "@/pages/logs";

import ConnectionsPage from "@/pages/connections";
import TagManagerListPage from "@/pages/tag-manager/list";
import FlowTemplatesListPage from "@/pages/assets/flow-templates/list";
import FlowsListPage from "@/pages/flows/list";
import DomainsListPage from "@/pages/settings/domains-list";
import WorkspaceSettingsPage from "@/pages/settings/workspace";
import GoalsSettingsPage from "@/pages/settings/goals";
import { RequirePlatformAdmin } from "@/components/auth/require-role";
import { AdminSection } from "@/pages/sections";
import { SettingsSection } from "@/pages/settings/section";
import HealthPage from "@/pages/health";
import NotFoundPage from "@/pages/not-found";
import { JournalShell } from "@/pages/journal/shell";

// FlowTemplateEditPage + FlowEditPage pull in xyflow, blueprint editor + inspector. Lazy
// load so the JS lands only when an operator actually opens an editor; this
// keeps the dashboard / list pages well under the chunk budget.
const FlowTemplateEditPage = lazy(
  () => import("@/pages/assets/flow-templates/edit"),
);
const FlowEditPage = lazy(() => import("@/pages/flows/edit"));
const CampaignEditorPage = lazy(() => import("@/pages/campaign/editor"));
const LanderDetailPage = lazy(() => import("@/pages/assets/landers/detail"));
// Reporting log pages pull in the wide server-paginated tables + drawers;
// lazy-split so the dashboard chunk stays lean.
const ReportsPage = lazy(() => import("@/pages/reports"));
const ClicksJournal = lazy(() => import("@/pages/journal/clicks"));
const BotsJournal = lazy(() =>
  import("@/pages/journal/clicks").then((m) => ({ default: m.BotsJournal })),
);
const ConversionsJournal = lazy(() => import("@/pages/journal/conversions"));
const PostbacksJournal = lazy(() => import("@/pages/journal/postbacks"));
const RulesPage = lazy(() => import("@/pages/rules"));
const FraudPage = lazy(() => import("@/pages/fraud"));
const IpIntelPage = lazy(() => import("@/pages/settings/ip-intel"));
const EdgePage = lazy(() => import("@/pages/settings/edge"));
const AdLinkTemplatesPage = lazy(
  () => import("@/pages/settings/ad-link-templates"),
);
const FraudTuningPage = lazy(() => import("@/pages/admin/fraud-tuning"));

function EditorLoadingFallback() {
  return (
    <div className="space-y-3 p-4 md:p-6">
      <Skeleton className="h-10 w-72" />
      <Skeleton className="h-150 w-full rounded-2xl" />
    </div>
  );
}

/** A lazy screen, with the one fallback this app uses. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<EditorLoadingFallback />}>{children}</Suspense>;
}

/**
 * An admin screen. The gate is on the ROUTE, not on the section layout: a
 * layout is a place to put tabs, and a place to put tabs is not a permission.
 * Deep-linking any of these without the role lands on the buyer's campaigns
 * list rather than on a blank page.
 */
function Admin({ children }: { children: ReactNode }) {
  return (
    <RequirePlatformAdmin fallback={<Navigate to="/campaigns" replace />}>
      {children}
    </RequirePlatformAdmin>
  );
}

/**
 * A DATA router, so a screen can hold a navigation — the editor's «Уйти без
 * сохранения?» (board EUnsaved) asks through `useBlocker`, which only a data
 * router has, and that is what catches the browser's Back. The routes stay
 * the JSX below under one splat route; nothing here loads data.
 */
const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }]);

export default function App() {
  return (
    <>
      <RouterProvider router={router} />
      <Toaster position="bottom-right" />
    </>
  );
}

function AppRoutes() {
  return (
    <>
      <Routes>
        {/* Realm customers, SSO-first, one org with the tracker (shell/sign-in.tsx). */}
        <Route element={<SignedInArea />}>
          {/* The org's sign-in link (console «Ссылка входа»): the gate reads the slug and drops it. */}
          <Route path="/o/*" element={null} />
          <Route element={<TrackerShell />}>
            {/* Every screen owns the viewport: it renders a <Page>, and the only
              thing that scrolls is a container it names. */}
            <Route index element={<DashboardPage />} />
            <Route path="campaigns" element={<CampaignsListPage />} />
            {/* The card's tabs are addresses (decision 04, lib/route-tab):
              /campaigns/:id is «Сводка», /campaigns/:id/flow «Поток», … */}
            <Route path="campaigns/:id" element={<CampaignCardPage />} />
            <Route path="campaigns/:id/:tab" element={<CampaignCardPage />} />
            {/* The editor — the ONE screen that creates and changes a campaign
              (решение 05): «+ Новая кампания» and the card's «Изменить»;
              the section is the hash (#rules), the link after creating is
              /campaigns/new?created=<id>. */}
            <Route
              path="campaigns/new"
              element={
                <Lazy>
                  <CampaignEditorPage />
                </Lazy>
              }
            />
            <Route
              path="campaigns/:id/edit"
              element={
                <Lazy>
                  <CampaignEditorPage />
                </Lazy>
              }
            />

            {/* Библиотека — what a campaign is assembled from, one rail row each
              (decision 03): Офферы, Ленды, Партнёрки, Источники, Домены. */}
            <Route
              path="library"
              element={<Navigate to="/library/offers" replace />}
            />
            {/* An offer and a partner open in a drawer over their list (decision
              08): the card, its tabs, «new» and «edit» are addresses of the
              same screen. */}
            <Route path="library/offers" element={<OffersPage />} />
            <Route path="library/offers/:id" element={<OffersPage />} />
            <Route path="library/offers/:id/:tab" element={<OffersPage />} />
            <Route path="library/landers" element={<LandersListPage />} />
            <Route
              path="library/sources"
              element={<TrafficSourcesListPage />}
            />
            <Route path="library/networks" element={<PartnersPage />} />
            <Route path="library/networks/:id" element={<PartnersPage />} />
            <Route
              path="library/networks/:id/:tab"
              element={<PartnersPage />}
            />
            <Route
              path="library/landers/:id"
              element={
                <Lazy>
                  <LanderDetailPage />
                </Lazy>
              }
            />
            {/* A lander's tabs are addresses too: Просмотр (the bare path),
              numbers, versions, settings, usage (доска LanderCard). */}
            <Route
              path="library/landers/:id/:tab"
              element={
                <Lazy>
                  <LanderDetailPage />
                </Lazy>
              }
            />
            {/* A source opens in its drawer over the list (доска SourceEditor). */}
            <Route
              path="library/sources/:id"
              element={<TrafficSourcesListPage />}
            />

            {/* Отчёты — one report: any grouping, filters, saved views; the
              day of week × hour grid is its «По гео и часам» view. */}
            <Route
              path="stats"
              element={
                <Lazy>
                  <ReportsPage />
                </Lazy>
              }
            />
            <Route
              path="stats/geo"
              element={<Navigate to="/stats?view=time" replace />}
            />

            {/* Журнал — the rows behind the numbers: clicks (people), conversions,
              every partner postback, bots. */}
            <Route path="journal" element={<JournalShell />}>
              <Route
                index
                element={<Navigate to="/journal/conversions" replace />}
              />
              <Route
                path="clicks"
                element={
                  <Lazy>
                    <ClicksJournal />
                  </Lazy>
                }
              />
              <Route
                path="conversions"
                element={
                  <Lazy>
                    <ConversionsJournal />
                  </Lazy>
                }
              />
              <Route
                path="postbacks"
                element={
                  <Lazy>
                    <PostbacksJournal />
                  </Lazy>
                }
              />
              <Route
                path="bots"
                element={
                  <Lazy>
                    <BotsJournal />
                  </Lazy>
                }
              />
            </Route>

            {/* Not /health: that path is the API's liveness probe (vibe-common
              platform), so a reload or a shared link there got JSON. */}
            <Route path="data-health" element={<HealthPage />} />

            <Route path="domains" element={<DomainsListPage />} />

            <Route path="settings" element={<SettingsSection />}>
              <Route index element={<WorkspaceSettingsPage />} />
              <Route path="goals" element={<GoalsSettingsPage />} />
              <Route path="connections" element={<ConnectionsPage />} />
              {/* Who changed what in the workspace — the audit log. */}
              <Route path="history" element={<LogsPage />} />
              {/* «Оповещения» (решение 09) — the rules that watch the numbers. */}
              <Route
                path="alerts"
                element={
                  <Lazy>
                    <RulesPage />
                  </Lazy>
                }
              />
            </Route>

            {/* Админка. Every screen under it changes something outside the
              buyer's workspace: routing templates, tag snippets injected into
              landers, the scoring weights every tenant is judged by, the edge's
              own configuration. Tag Manager and the fraud weights used to be
              reachable by anyone with a login. */}
            <Route
              path="admin"
              element={
                <Admin>
                  <AdminSection />
                </Admin>
              }
            >
              <Route index element={<Navigate to="/admin/flows" replace />} />
              <Route
                path="flows"
                element={
                  <Admin>
                    <FlowsListPage />
                  </Admin>
                }
              />
              <Route
                path="flow-templates"
                element={
                  <Admin>
                    <FlowTemplatesListPage />
                  </Admin>
                }
              />
              <Route
                path="tag-manager"
                element={
                  <Admin>
                    <TagManagerListPage />
                  </Admin>
                }
              />
              {/* The detectors' own view of traffic quality: reputation and
                whether a verdict predicts conversions. The buyer's bots are
                the journal's «Боты» tab (traffic class). */}
              <Route
                path="detectors"
                element={
                  <Admin>
                    <Lazy>
                      <FraudPage />
                    </Lazy>
                  </Admin>
                }
              />
              <Route
                path="fraud-tuning"
                element={
                  <Admin>
                    <Lazy>
                      <FraudTuningPage />
                    </Lazy>
                  </Admin>
                }
              />
              <Route
                path="ad-link-templates"
                element={
                  <Admin>
                    <Lazy>
                      <AdLinkTemplatesPage />
                    </Lazy>
                  </Admin>
                }
              />
              <Route
                path="ip-intel"
                element={
                  <Admin>
                    <Lazy>
                      <IpIntelPage />
                    </Lazy>
                  </Admin>
                }
              />
              <Route
                path="edge"
                element={
                  <Admin>
                    <Lazy>
                      <EdgePage />
                    </Lazy>
                  </Admin>
                }
              />
            </Route>
            {/* The flow editors are full-screen authoring surfaces, so they sit
              beside the admin section rather than inside its tab strip. */}
            <Route
              path="admin/flows/new"
              element={
                <Admin>
                  <Lazy>
                    <FlowEditPage />
                  </Lazy>
                </Admin>
              }
            />
            <Route
              path="admin/flows/:id/edit"
              element={
                <Admin>
                  <Lazy>
                    <FlowEditPage />
                  </Lazy>
                </Admin>
              }
            />
            <Route
              path="admin/flow-templates/new"
              element={
                <Admin>
                  <Lazy>
                    <FlowTemplateEditPage />
                  </Lazy>
                </Admin>
              }
            />
            <Route
              path="admin/flow-templates/:id/edit"
              element={
                <Admin>
                  <Lazy>
                    <FlowTemplateEditPage />
                  </Lazy>
                </Admin>
              }
            />

            {/* Addresses #227 moved today (geo → the report's time view, the
              event log → settings/history). */}
            <Route
              path="geo"
              element={<Navigate to="/stats?view=time" replace />}
            />
            <Route
              path="journal/events"
              element={<Navigate to="/settings/history" replace />}
            />
            {/* An address with no screen is a 404 in the frame (board TEdges), not a silent jump home. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}
